import { datadogRum } from '@datadog/browser-rum';
import { Chip, ListItemText, Stack, Typography } from '@mui/material';
import { format } from 'date-fns';
import { groupBy } from 'lodash';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useAuditLogOverviewQuery } from 'src/hooks/audit-log/queries/useAuditLogOverviewQuery';
import { useIsAuditLogSupported } from 'src/hooks/audit-log/useIsAuditLogSupported';
import { useCurrentModule } from 'src/hooks/useCurrentModule';
import { useDrawerState } from 'src/hooks/useDrawerState';
import { AuditLogListItem, BaseAuditLogDrawer } from '../BaseAuditLogDrawer/BaseAuditLogDrawer';

export const AuditLogDrawer: FC = () => {
    const { auditLogDrawerOpen, setAuditLogDrawerOpen } = useDrawerState();
    const module = useCurrentModule();
    const { data: auditLogs, loading, error } = useAuditLogOverviewQuery(auditLogDrawerOpen) ?? {};

    const isAuditLogSupported = useIsAuditLogSupported();

    useEffect(() => {
        if (!isAuditLogSupported) {
            setAuditLogDrawerOpen(false);
        }
    }, [isAuditLogSupported, setAuditLogDrawerOpen]);

    const groupedByDate = useMemo(
        () => groupBy(auditLogs, (auditLog) => auditLog.timeOfUpdate.split('T')[0]),
        [auditLogs]
    );

    const groupHeaders = useMemo(() => Object.keys(groupedByDate), [groupedByDate]);

    const closeDrawer = useCallback(() => {
        setAuditLogDrawerOpen(false);
        datadogRum.addAction('audit-log', {
            module: module,
            open: false,
        });
    }, [setAuditLogDrawerOpen, module]);

    return (
        <BaseAuditLogDrawer
            onClose={closeDrawer}
            open={auditLogDrawerOpen}
            error={error ?? null}
            loading={loading ?? false}
            isEmpty={!loading && groupHeaders.length <= 0}
        >
            <Stack
                sx={{
                    flex: 1,
                    gap: 2,
                    overflow: 'auto',
                    width: 475,
                }}
            >
                {groupHeaders.map((header) => {
                    const auditLogs = groupedByDate[header];
                    return (
                        <AuditLogListItem key={header}>
                            <ListItemText
                                primary={
                                    <Typography variant="subtitle1">
                                        {format(new Date(header), 'LLLL d, yyyy')}
                                    </Typography>
                                }
                                secondary={
                                    <Stack
                                        sx={{
                                            mt: 1,
                                            gap: 2,
                                        }}
                                    >
                                        {auditLogs.map((auditLog) => {
                                            return (
                                                <Stack key={auditLog.correlationId}>
                                                    <Stack
                                                        sx={{
                                                            gap: 0,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            color="primary"
                                                            sx={{
                                                                fontWeight: 'bold',
                                                            }}
                                                        >
                                                            {auditLog.changesCount} change(s)
                                                        </Typography>
                                                        <Stack
                                                            direction="row"
                                                            sx={{
                                                                alignItems: 'center',
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    fontWeight: 'bold',
                                                                }}
                                                            >
                                                                {format(new Date(auditLog.timeOfUpdate), 'HH:mm ')}
                                                            </Typography>
                                                            <Typography>{auditLog.userId}</Typography>
                                                        </Stack>

                                                        {auditLog.auditLogType === 'SplitLevel' &&
                                                            auditLog.details?.map((change) => (
                                                                <Stack
                                                                    direction="row"
                                                                    key={change.field}
                                                                    sx={{
                                                                        gap: 1,
                                                                        mt: 2,
                                                                        alignItems: 'center',
                                                                    }}
                                                                >
                                                                    <Chip
                                                                        sx={{ fontSize: '11px' }}
                                                                        label={'Inheritance'}
                                                                    />
                                                                    <Chip
                                                                        label={change.oldValue as string}
                                                                        variant="outlined"
                                                                    />
                                                                    &rarr;
                                                                    <Chip
                                                                        label={change.newValue as string}
                                                                        variant="outlined"
                                                                    />
                                                                </Stack>
                                                            ))}
                                                    </Stack>
                                                </Stack>
                                            );
                                        })}
                                    </Stack>
                                }
                                slotProps={{
                                    secondary: { component: 'div' },
                                }}
                            />
                        </AuditLogListItem>
                    );
                })}
            </Stack>
        </BaseAuditLogDrawer>
    );
};
